import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/UserAvatar";
import { Heart, MessageCircle, MoreHorizontal, Share2, Send, Loader2, Image as ImageIcon, Smile, AtSign, Hash, Plus, Search, X } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useConfirm } from "@/hooks/useConfirm";
import EmojiPicker from 'emoji-picker-react';
import imageCompression from 'browser-image-compression';


const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY;

export function FeedItem({ recognition, currentUser, users = [], companyValues = [] }) {
  // const { user: currentUser } = useAuth(); // Removed useAuth
  const { confirm, ConfirmDialog } = useConfirm();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(recognition.reactions?.length || 0);

  // Comment State
  const [comments, setComments] = useState(recognition.comments || []);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isSendingComment, setIsSendingComment] = useState(false);

  // Comment Attachment & Media
  const [attachment, setAttachment] = useState(null); // { type: 'gif' | 'image', url: string, file?: File }
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [giphySearch, setGiphySearch] = useState("");
  const [gifs, setGifs] = useState([]);
  const [isLoadingGifs, setIsLoadingGifs] = useState(false);
  const fileInputRef = useRef(null);

  const isCelebration = !!recognition.celebration_event;

  const getBackgroundStyle = () => {
    if (!isCelebration) return {};

    const event = recognition.celebration_event;

    if (event.background_url) {
      return {
        backgroundImage: `url(${event.background_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }

    if (event.background_image_url && !event.background_image_url.startsWith('linear-gradient')) {
      return {
        backgroundImage: `url(${event.background_image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }

    const gradients = {
      'birthday': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'work_anniversary': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'new_user': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'custom_date': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    };

    return {
      background: gradients[event.trigger_type] || gradients['custom_date']
    };
  };

  useEffect(() => {
    // Check if current user liked this
    if (currentUser && recognition.reactions) {
      const hasLiked = recognition.reactions.some(r => r.user_id === currentUser.id);
      setLiked(hasLiked);
    }
  }, [currentUser, recognition.reactions]);

  const handleLike = async () => {
    if (!currentUser) return;

    // Optimistic Update
    const checkLike = !liked;
    setLiked(checkLike);
    setLikesCount(checkLike ? likesCount + 1 : likesCount - 1);

    try {
      /*
      if (checkLike) {
        // Insert Like
        // await supabase...
      } else {
        // Remove Like
        // await supabase...
      }
      */
    } catch (err) {
      console.error("Error toggling like:", err);
      // Revert if error
      setLiked(!checkLike);
      setLikesCount(!checkLike ? likesCount + 1 : likesCount - 1);
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || !currentUser) return;
    setIsSendingComment(true);

    try {
      // Preparar FormData para enviar imagen si existe
      const formData = new FormData();
      formData.append('comment[content]', commentText);
      formData.append('comment[points]', 0);

      if (attachment?.type === 'gif') {
        formData.append('comment[gif_url]', attachment.url);
      } else if (attachment?.type === 'image' && attachment?.file) {
        formData.append('comment[image]', attachment.file);
      }

      // Enviar comentario al backend Rails
      const response = await fetch(`/recognitions/${recognition.id}/comments`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(', ') || 'Error al crear comentario');
      }

      const data = await response.json();

      // Update Local State
      setComments([...comments, data]);
      setCommentText("");
      setAttachment(null);

    } catch (err) {
      console.error("Error sending comment:", err);
      await confirm({
        title: 'Error al comentar',
        message: err.message || 'Error al enviar comentario.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'danger'
      });
    } finally {
      setIsSendingComment(false);
    }
  };

  const fetchGifs = async (type, query = '') => {
    if (!GIPHY_API_KEY) return;
    setIsLoadingGifs(true);
    try {
      const endpoint = type === 'search'
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${query}&limit=20&rating=g`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`;

      const res = await fetch(endpoint);
      const data = await res.json();
      setGifs(data.data || []);
    } catch (error) {
      console.error("Error fetching GIFs:", error);
    } finally {
      setIsLoadingGifs(false);
    }
  };

  const handleGiphySearch = (e) => {
    setGiphySearch(e.target.value);
    if (e.target.value.length > 2) {
      fetchGifs('search', e.target.value);
    } else if (e.target.value.length === 0) {
      fetchGifs('trending');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };

      const compressedFile = await imageCompression(file, options);
      const previewUrl = URL.createObjectURL(compressedFile);

      setAttachment({
        type: 'image',
        url: previewUrl,
        file: compressedFile
      });
    } catch (error) {
      console.error('Error compressing image:', error);
    }

    e.target.value = null;
  };

  const handleEmojiClick = (emojiObject) => {
    setCommentText(commentText + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  // Fetch trending GIFs when GIF picker opens
  useEffect(() => {
    if (showGifPicker && !giphySearch && GIPHY_API_KEY) {
      fetchGifs('trending');
    }
  }, [showGifPicker]);

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(date);
    } else if (days > 0) {
      return `Hace ${days}d`;
    } else if (hours > 0) {
      return `Hace ${hours}h`;
    } else if (minutes > 0) {
      return `Hace ${minutes}m`;
    } else {
      return "Hace un momento";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group"
    >
      {isCelebration && (
        <div
          className="relative px-6 py-12 flex flex-col items-center justify-center text-center overflow-hidden"
          style={getBackgroundStyle()}
        >
          <div className="absolute inset-0 bg-black/20"></div>

          <div className="relative z-10 mb-4">
            <UserAvatar
              user={recognition.receiver}
              src={recognition.receiver?.avatar_url}
              size={80}
              className="ring-4 ring-white shadow-2xl"
            />
          </div>

          <div className="relative z-10 max-w-md">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-xl">
              <div className="text-lg font-bold text-gray-900 mb-2">
                {recognition.receiver.full_name}
              </div>
              <div
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: recognition.message }}
              />
              <div className="mt-3 inline-flex items-center gap-2 bg-bambu-green text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                <span className="text-xl">+{recognition.points}</span>
                <span>puntos</span>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-3">
            <span className="text-xs text-white/90 font-medium bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
              {timeAgo(recognition.created_at)}
            </span>
          </div>
        </div>
      )}

      {!isCelebration && (
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Points Pill (Solid Green) */}
          <div className="bg-bambu-green text-white px-3 py-1 rounded-full font-extrabold text-sm shadow-sm flex items-center gap-1 cursor-pointer hover:scale-105 transition-transform hover:bg-bambu-dark-green select-none">
            <span className="text-lg leading-none">+</span>
            <span className="text-lg leading-none">{recognition.points}</span>
          </div>

          {/* Receiver Avatar(s) */}
          <div className="relative flex items-center">
            {recognition.recipients && recognition.recipients.length > 0 ? (
              // Multi-Receiver (Stacked)
              <div className="flex -space-x-3 hover:space-x-1 transition-all duration-300 p-1">
                {recognition.recipients.slice(0, 3).map((recipient, index) => (
                  <div key={index} className="relative group/avatar cursor-pointer hover:z-50 transition-all">
                    <UserAvatar
                      user={recipient}
                      src={recipient.avatar_url}
                      size={40}
                      className="ring-2 ring-white shadow-md relative z-10 transition-transform group-hover/avatar:scale-110"
                    />


                  </div>
                ))}
                {recognition.recipients.length > 3 && (
                  <div className="w-10 h-10 rounded-full bg-gray-100 ring-2 ring-white flex items-center justify-center text-xs font-bold text-gray-500 shadow-sm z-0">
                    +{recognition.recipients.length - 3}
                  </div>
                )}
              </div>
            ) : (
              // Single Receiver (Legacy)
              <div className="relative group/avatar cursor-pointer">
                <UserAvatar
                  user={recognition.receiver}
                  src={recognition.receiver?.avatar_url}
                  size={40}
                  className="ring-2 ring-white shadow-md transition-transform group-hover/avatar:scale-105"
                />

              </div>
            )}
          </div>

        </div>

        {/* Relative Date with Tooltip */}
        <div className="relative group/date cursor-pointer">
          <span className="text-xs text-gray-400 font-medium hover:text-gray-600 transition-colors">
            {timeAgo(recognition.created_at)}
          </span>
          {/* Simple Tooltip for Exact Date */}
          <div className="absolute top-full right-0 mt-1 z-50 px-2 py-1 bg-gray-800 text-white text-[10px] rounded shadow-lg opacity-0 invisible group-hover/date:visible group-hover/date:opacity-100 transition-all duration-200 whitespace-nowrap pointer-events-none">
            {new Date(recognition.created_at).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}
          </div>
        </div>
      </div>
      )}

      {/* CARD BODY: Content */}
      {!isCelebration && (
      <div className="px-6 pb-2">
        {/* Sender Name + Message inline */}
        <div className="text-gray-700 text-base leading-relaxed font-montserrat">
          <span className="font-bold text-gray-900 mr-2">{recognition.sender.full_name}:</span>
          <span
            className="font-medium"
            dangerouslySetInnerHTML={{ __html: recognition.message }}
          />
        </div>
      </div>
      )}

      {/* MEDIA ATTACHMENT - Full Width/Height Preserved but Contained */}
      {!isCelebration && (recognition.gif_url || recognition.image_url) && (
          <div className="mt-4 mb-2 mx-6">
            <div className="flex justify-center bg-gray-50 rounded-2xl overflow-hidden shadow-sm">
              <img
                src={recognition.image_url || recognition.gif_url}
                alt="Adjunto"
                className="w-full h-auto max-h-[500px] object-cover"
              />
            </div>
            {!recognition.image_url && recognition.gif_url && (
              <div className="flex justify-end mt-1 pr-1">
                <img src="/PoweredBy_Light.gif" alt="Powered by Giphy" className="h-6" />
              </div>
            )}
          </div>
        )}

      {/* ACTION FOOTER */}
      <div className="px-6 py-4 flex items-center justify-between border-t border-gray-50 mt-1">
        <div className="flex items-center gap-5">
          <button
            onClick={handleLike}
            className={cn(
              "flex items-center gap-2 transition-all active:scale-95 group/like cursor-pointer",
              liked ? "text-red-500" : "text-gray-400 hover:text-gray-600"
            )}
          >
            <div className={cn("p-2 rounded-full transition-colors", liked ? "bg-red-50" : "bg-gray-50 group-hover/like:bg-gray-100")}>
              <Heart className={cn("w-5 h-5 transition-transform", liked && "fill-current scale-110")} />
            </div>
            <span className="text-sm font-bold">{likesCount}</span>
          </button>

          <button
            onClick={() => setShowCommentInput(!showCommentInput)}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-all active:scale-95 group/comment cursor-pointer"
          >
            <div className={cn("p-2 rounded-full transition-colors", showCommentInput ? "bg-blue-50 text-blue-500" : "bg-gray-50 group-hover/comment:bg-gray-100")}>
              <MessageCircle className="w-5 h-5" />
            </div>
            <span className="text-sm font-bold">{comments.length > 0 ? comments.length : "Comentar"}</span>
          </button>
        </div>

        {/* Share Button (Optional) */}
        {/* <button className="text-gray-300 hover:text-gray-500">
            <Share2 className="w-5 h-5" />
        </button> */}
      </div>

      {/* COMMENTS SECTION */}
      <AnimatePresence>
        {(showCommentInput || comments.length > 0) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-50 border-t border-gray-100 px-6 py-6 space-y-4"
          >
            {/* Comment Input */}
            {showCommentInput && (
              <div className="flex gap-3 items-start pt-2">
                <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0 overflow-hidden">
                  {/* Current user avatar placeholder if loading */}
                  {currentUser && <UserAvatar user={{ ...currentUser, full_name: currentUser.user_metadata?.full_name, avatar_url: currentUser.user_metadata?.avatar_url }} size={32} />}
                </div>
                <div className="flex-1 space-y-2">
                  {/* Attachment Preview */}
                  {attachment && (
                    <div className="relative group">
                      <img
                        src={attachment.url}
                        className="w-full h-auto max-h-60 object-contain rounded-xl border border-gray-200 bg-gray-50"
                        alt="Attachment"
                      />
                      <button
                        onClick={() => setAttachment(null)}
                        className="absolute -top-2 -right-2 bg-white text-gray-400 hover:text-red-500 rounded-full p-1.5 shadow-md border transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  <div className="relative">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Agradecer..."
                      className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-bambu-green/20 focus:border-bambu-green/50 min-h-11 resize-none pb-12 shadow-inner scrollbar-hide"
                      rows={Math.max(1, commentText.split('\n').length)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleCommentSubmit();
                        }
                      }}
                    />

                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                      <div className="flex gap-1.5">
                        {/* GIF Button */}
                        <button
                          onClick={() => setShowGifPicker(!showGifPicker)}
                          className={cn("p-1.5 rounded-lg transition-all text-xs font-bold", showGifPicker ? "bg-bambu-green text-white" : "text-gray-400 hover:bg-gray-100")}
                          title="Agregar GIF"
                        >
                          GIF
                        </button>

                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-all"
                          title="Agregar imagen"
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />

                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className={cn("p-1.5 rounded-lg transition-all", showEmojiPicker ? "bg-amber-100 text-amber-500" : "text-gray-400 hover:bg-gray-100")}
                          title="Agregar emoji"
                        >
                          <Smile className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={handleCommentSubmit}
                        disabled={!commentText.trim() || isSendingComment}
                        className="p-1.5 text-bambu-green hover:bg-bambu-green-pastel rounded-lg transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Enviar comentario"
                      >
                        {isSendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </div>

                    {showEmojiPicker && (
                      <div className="absolute bottom-full left-0 mb-2 z-50 animate-in fade-in zoom-in duration-200">
                        <EmojiPicker
                          onEmojiClick={handleEmojiClick}
                          width={300}
                          height={350}
                          previewConfig={{ showPreview: false }}
                        />
                      </div>
                    )}

                    {showGifPicker && (
                      <div className="absolute bottom-full left-0 right-0 mb-2 h-80 bg-white border border-gray-200 shadow-2xl rounded-2xl z-50 p-4 flex flex-col">
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Buscar en GIPHY..."
                            className="w-full bg-gray-50 rounded-xl py-2 pl-9 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-bambu-green/20"
                            value={giphySearch}
                            onChange={handleGiphySearch}
                            autoFocus
                          />
                          <div className="absolute right-3 top-2.5">
                            <span className="text-[10px] font-bold text-gray-300">GIPHY</span>
                          </div>
                        </div>

                        <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-2">
                          {isLoadingGifs ? (
                            <div className="col-span-2 flex items-center justify-center h-full text-gray-400">
                              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
                            </div>
                          ) : gifs.length > 0 ? (
                            gifs.map(gif => (
                              <img
                                key={gif.id}
                                src={gif.images.fixed_height_small.url}
                                alt={gif.title}
                                className="w-full h-auto rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => {
                                  setAttachment({ type: 'gif', url: gif.images.fixed_height.url });
                                  setShowGifPicker(false);
                                }}
                              />
                            ))
                          ) : (
                            <div className="col-span-2 flex items-center justify-center h-full text-gray-400 text-sm">
                              {GIPHY_API_KEY ? "No se encontraron GIFs" : "Falta configuración de API Key"}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {comments.length > 0 && (
              <div className="space-y-4 pt-2">
                {comments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((comment) => (
                  <div key={comment.id} className="flex gap-3 animate-in fade-in slide-in-from-top-1">
                    <UserAvatar user={comment.user} size={32} className="shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="bg-white rounded-2xl rounded-tl-none px-4 py-2.5 shadow-sm border border-gray-100 inline-block max-w-full">
                        <span className="font-bold text-sm text-gray-900 mr-2">{comment.user?.full_name}</span>
                        <span className="text-sm text-gray-700">{comment.content}</span>
                      </div>

                      {(comment.image_url || comment.gif_url) && (
                        <div className="mt-2">
                          <img
                            src={comment.image_url || comment.gif_url}
                            alt="Comment attachment"
                            className="max-w-sm w-full h-auto rounded-xl border border-gray-200 shadow-sm"
                          />
                        </div>
                      )}

                      {/* Comment Meta */}
                      <div className="ml-1 mt-1 flex items-center gap-3 text-[10px] text-gray-400 font-bold uppercase tracking-wide">
                        <span>{timeAgo(comment.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog />
    </motion.div >
  );
}
